import React, { FC, useRef, useState, useEffect, PropsWithChildren } from 'react';
import L, { DragEndEvent, LeafletMouseEvent } from 'leaflet';
import { withStyles, createStyles, WithStyles } from '@material-ui/core';

interface MapProps extends WithStyles<typeof styles>, PropsWithChildren {
  coords?: [number, number];
  points?: GeoPoint[];
  cursor?: string;
  onMapClick?: (e: LeafletMouseEvent) => void;
  onDragOnMap?: (e: DragEndEvent) => void;
}

const SimpleMap: FC<MapProps> = (props) => {
  const { classes, coords, children, points, cursor, onDragOnMap, onMapClick } = props;

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const markers = useRef<L.Marker[]>([]);
  const [map, setMap] = useState<L.Map | null>(null);

  useEffect(() => {
    if (mapContainerRef.current && !map) {
      const _map = L.map(mapContainerRef.current, {
        zoomControl: false,
      }).setView(coords || [55.8, 37.8], 13);
      setMap(_map);
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(
        _map
      );
      coords &&
        L.marker(coords, {
          icon: new L.Icon({
            iconUrl: '/images/markers/location.png',
          }),
        }).addTo(_map);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map) {
      return;
    }
    onDragOnMap && map.on('dragend', onDragOnMap);
    onMapClick && map.on('click', onMapClick);
  }, [map, onDragOnMap, onMapClick]);

  useEffect(() => {
    if (points && map) {
      markers.current.forEach((marker) => marker.remove());
      markers.current = [];

      const lats: number[] = [];
      const lngs: number[] = [];
      points.forEach((point) => {
        if (
          !point.geometry.coordinates?.[0] ||
          !point.geometry.coordinates?.[1]
        ) {
          return;
        }

        const marker = L.marker(
          point.geometry.coordinates as [number, number],
          {
            icon: new L.Icon({
              iconUrl:
                point.geometry.type === 'point'
                  ? '/images/markers/empty-positive.png'
                  : '/images/markers/empty-selected.png',
              iconSize: [20, 36],
              shadowSize: [30, 15],
              iconAnchor: [10, 36],
              shadowAnchor: [0, 15],
              popupAnchor: [8, 30],
            }),
            draggable: point.properties.draggable,
          }
        ).addTo(map);

        if (point.properties.draggable && onDragOnMap) {
          marker.on('dragend', onDragOnMap);
        }

        markers.current.push(marker);
        lats.push(point.geometry.coordinates[0]);
        lngs.push(point.geometry.coordinates[1]);
      });

      if (!lats.length || !lngs.length) {
        return;
      }

      // calc the min and max lng and lat
      const minlat = Math.min.apply(null, lats),
        maxlat = Math.max.apply(null, lats);
      const minlng = Math.min.apply(null, lngs),
        maxlng = Math.max.apply(null, lngs);

      // create a bounding rectangle that can be used in leaflet
      const bbox: [[number, number], [number, number]] = [
        [minlat, minlng],
        [maxlat, maxlng],
      ];

      map.fitBounds(bbox);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, map]);

  return (
    <>
      <div ref={mapContainerRef} className={classes.mapContainer} style={{ cursor }} />
      <div className={classes.controls}>{children}</div>
    </>
  );
};

const styles = () =>
  createStyles({
    mapContainer: {
      width: '100%',
      height: '100%',
      position: 'absolute',
      '& .leaflet-right': {
        zIndex: 1001,
      },
    },
    controls: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      width: '300px',
      display: 'flex',
      justifyContent: 'center',
      zIndex: 1000,
    },
  });

export default withStyles(styles)(SimpleMap);
